<template>
    <div class="page-container">
        <div class="settings-form">
            <div class="header-section">
                <h2 class="page-title">名片管理</h2>

                <div class="stats-info">
                    <div class="stat-item">
                        <span class="label">总名片数：</span>
                        <span class="value">{{ stats.total }}</span>
                    </div>
                    <div class="stat-item">
                        <span class="label">公开名片：</span>
                        <span class="value">{{ stats.public }}</span>
                    </div>
                    <div class="stat-item">
                        <span class="label">主名片：</span>
                        <span class="value primary">{{ primaryCardName }}</span>
                    </div>
                </div>
            </div>


            <!-- 搜索和操作区域 -->
            <div class="search-bar">
                <el-input v-model="searchForm.name" placeholder="请输入姓名搜索" clearable size="large"
                    style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <el-button type="primary" size="large" @click="handleSearch">
                    <el-icon>
                        <Search />
                    </el-icon>
                    搜索
                </el-button>

                <el-button type="success" size="large" @click="openAddDialog" style="float: right">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    新增名片
                </el-button>

                <el-button type="info" size="large" @click="fetchCardList" :loading="loading" style="margin-left: 15px">
                    <el-icon>
                        <Refresh />
                    </el-icon>
                    刷新
                </el-button>
            </div>

            <!-- 名片列表 -->
            <el-table :data="cardList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
                <el-table-column label="主名片" width="80" align="center">
                    <template #default="{ row }">
                        <el-tooltip :content="row.is_primary ? '主名片' : '设为首页'" placement="top">
                            <el-icon :size="20" :color="row.is_primary ? '#67C23A' : '#909399'" class="primary-icon"
                                @click="setAsPrimary(row)" style="cursor: pointer;">
                                <StarFilled v-if="row.is_primary" />
                                <Star v-else />
                            </el-icon>
                        </el-tooltip>
                    </template>
                </el-table-column>

                <el-table-column label="头像" width="80" align="center">
                    <template #default="{ row }">
                        <el-avatar :size="40" :src="row.avatar || row.user_avatar" :alt="row.name">
                            {{ row.name.charAt(0) }}
                        </el-avatar>
                    </template>
                </el-table-column>

                <el-table-column prop="name" label="姓名" width="120" />
                <el-table-column label="关联用户" width="150">
                    <template #default="{ row }">
                        <div class="user-info">
                            <div class="user-nickname">{{ row.user_nickname || '未设置' }}</div>
                            <div class="user-phone">{{ row.user_phone }}</div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="TX广告" width="180">
                    <template #default="{ row }">
                        <div v-if="row.gzh_name" class="gzh-info">
                            <div class="gzh-name">{{ row.gzh_name }}</div>
                            <div class="gzh-appid">{{ row.gzh_appid }}</div>
                        </div>
                        <span v-else class="empty-value">未关联</span>
                    </template>
                </el-table-column>
                <el-table-column prop="title" label="职位" width="150">
                    <template #default="{ row }">
                        <span v-if="row.title">{{ row.title }}</span>
                        <span v-else class="empty-value">未设置</span>
                    </template>
                </el-table-column>
                <el-table-column prop="company" label="公司" width="180">
                    <template #default="{ row }">
                        <span v-if="row.company">{{ row.company }}</span>
                        <span v-else class="empty-value">未设置</span>
                    </template>
                </el-table-column>

                <!-- 在名片列表的 el-table-column label="专属链接" 中修改 -->
                <el-table-column label="专属链接" width="350">
                    <template #default="{ row }">
                        <div class="card-url-section">
                            <el-popover placement="bottom" title="名片访问链接" :width="450" trigger="click" v-if="row.id">
                                <template #reference>
                                    <el-button type="primary" link style="padding: 8px 12px;">查看链接</el-button>
                                </template>
                                <div class="url-list">
                                    <div class="url-item">
                                        <span class="url-label">公众号链接：</span>
                                        <div class="url-content">
                                            <span class="url-text">{{ getOffiaccountUrl(row.id) }}</span>
                                            <el-button type="text" size="small"
                                                @click="copyUrl(getOffiaccountUrl(row.id))">
                                                复制
                                            </el-button>
                                        </div>
                                    </div>
                                    <div class="url-item">
                                        <span class="url-label">H5链接：</span>
                                        <div class="url-content">
                                            <span class="url-text">{{ getH5Url(row.id) }}</span>
                                            <el-button type="text" size="small" @click="copyUrl(getH5Url(row.id))">
                                                复制
                                            </el-button>
                                        </div>
                                    </div>
                                    <div class="url-item">
                                        <span class="url-label">小程序路径：</span>
                                        <div class="url-content">
                                            <div
                                                style="display: flex; justify-content: space-between; align-items: center;">
                                                <span class="url-text">{{ getMiniUrl(row.id) }}</span>
                                                <div class="mini-qrcode-actions">
                                                    <el-button type="text" size="small"
                                                        @click="copyUrl(getMiniUrl(row.id))">
                                                        复制
                                                    </el-button>
                                                    <el-button type="text" size="small"
                                                        @click.stop="generateMiniprogramQrcode(row)"
                                                        :loading="row.qrcode_loading" v-if="hasMiniprogramConfig">
                                                        <el-icon>
                                                            <Picture />
                                                        </el-icon>
                                                        {{ row.miniprogram_qrcode || row.miniprogram_qrcode_filename ?
                                                            '更新' : '生成' }}
                                                    </el-button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- 显示小程序二维码（如果已生成） -->
                                    <div class="qrcode-display" v-if="row.miniprogram_qrcode && !row.qrcode_loading">
                                        <div class="qrcode-title">
                                            <el-icon>
                                                <Picture />
                                            </el-icon>
                                            <span>小程序二维码</span>
                                            <el-tag size="small" type="success" style="margin-left: 8px">永久有效</el-tag>
                                        </div>
                                        <div class="qrcode-content">
                                            <img :src="row.miniprogram_qrcode" alt="小程序二维码" class="qrcode-image"
                                                @click="previewQrcode(row.miniprogram_qrcode, row.name)" />
                                            <div class="qrcode-actions">
                                                <el-button type="text" size="small"
                                                    @click="downloadQrcode(row.miniprogram_qrcode, row.name)">
                                                    <el-icon>
                                                        <Download />
                                                    </el-icon>
                                                    下载
                                                </el-button>
                                                <el-button type="text" size="small"
                                                    @click="copyUrl(row.miniprogram_qrcode)">
                                                    <el-icon>
                                                        <DocumentCopy />
                                                    </el-icon>
                                                    复制链接
                                                </el-button>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- 小程序码生成状态提示 -->
                                    <div class="qrcode-tip" v-if="row.qrcode_loading">
                                        <el-icon class="is-loading">
                                            <Loading />
                                        </el-icon>
                                        生成小程序码中...
                                    </div>
                                </div>
                            </el-popover>
                            <span v-else class="empty-value">未生成</span>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="状态" width="100">
                    <template #default="{ row }">
                        <el-tag :type="row.is_public ? 'success' : 'info'" size="small">
                            {{ row.is_public ? '公开' : '私密' }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column prop="create_time" label="创建时间" width="180" />
                <el-table-column prop="update_time" label="更新时间" width="180" />

                <el-table-column label="操作" width="320" fixed="right">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
                            <el-icon>
                                <Edit />
                            </el-icon>
                            编辑
                        </el-button>
                        <el-button type="warning" size="small" @click="openProductDialog(scope.row)">
                            <el-icon>
                                <Goods />
                            </el-icon>
                            产品
                        </el-button>
                        <!-- 新增广告按钮 -->
                        <el-button type="info" size="small" @click="openAdDialog(scope.row)">
                            <el-icon>
                                <Promotion />
                            </el-icon>
                            广告
                        </el-button>

                        <el-button type="danger" size="small" @click="handleDelete(scope.row.id)"
                            :disabled="scope.row.is_primary">
                            <el-icon>
                                <Delete />
                            </el-icon>
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                    layout="total, sizes, prev, pager, next, jumper" @size-change="fetchCardList"
                    @current-change="fetchCardList" />
            </div>
        </div>

        <!-- 新增广告管理对话框 -->
        <el-dialog v-model="adDialogVisible" :title="`广告人群管理 - ${currentCard?.name}`" width="1000px"
            :close-on-click-modal="false" class="ad-audience-dialog">

            <!-- Tab标签页 -->
            <el-tabs v-model="activePlatform" class="platform-tabs" @tab-click="handlePlatformChange">
                <el-tab-pane label="腾讯广告" name="tx">
                    <div v-if="activePlatform === 'tx'">
                        <!-- 腾讯广告子Tab -->
                        <el-tabs v-model="activeTxTab" type="border-card" @tab-change="handleTxTabChange">
                            <el-tab-pane label="OpenID人群绑定" name="openid">
                                <TxAudienceTab ref="openidTab" :card="currentCard" :bind-type="'openid'"
                                    :platform="'tx'" @refresh="fetchCardAudiences" />
                            </el-tab-pane>
                            <el-tab-pane label="手机号人群绑定" name="mobile">
                                <TxAudienceTab ref="mobileTab" :card="currentCard" :bind-type="'mobile'"
                                    :platform="'tx'" @refresh="fetchCardAudiences" />
                            </el-tab-pane>
                        </el-tabs>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="抖音广告" name="douyin">
                    <div class="coming-soon">
                        <el-result icon="info" title="功能开发中" sub-title="程序猿正在紧急开发中···，请稍后！">
                            <template #extra>
                                <el-button type="primary" @click="handleContact">联系客服</el-button>
                            </template>
                        </el-result>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="百度广告" name="baidu">
                    <div class="coming-soon">
                        <el-result icon="info" title="功能开发中" sub-title="程序猿正在紧急开发中···，请稍后！">
                            <template #extra>
                                <el-button type="primary" @click="handleContact">联系客服</el-button>
                            </template>
                        </el-result>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="小红书广告" name="xiaohongshu">
                    <div class="coming-soon">
                        <el-result icon="info" title="功能开发中" sub-title="程序猿正在紧急开发中···，请稍后！">
                            <template #extra>
                                <el-button type="primary" @click="handleContact">联系客服</el-button>
                            </template>
                        </el-result>
                    </div>
                </el-tab-pane>
            </el-tabs>

            <!-- 已绑定人群列表 -->
            <div class="bound-audiences-section">
                <h3 class="section-title">已绑定人群</h3>
                <el-table :data="boundAudiences" stripe style="width: 100%" v-loading="audienceLoading" height="300">
                    <el-table-column label="平台" width="100">
                        <template #default="{ row }">
                            <el-tag :type="getPlatformTagType(row.platform)" size="small">
                                {{ getPlatformText(row.platform) }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column prop="audience_name" label="人群名称" width="200" show-overflow-tooltip />

                    <el-table-column label="绑定类型" width="120">
                        <template #default="{ row }">
                            <el-tag :type="row.bind_type === 'openid' ? 'success' : 'primary'" size="small">
                                {{ row.bind_type === 'openid' ? 'OpenID' : '手机号' }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="人群状态" width="100">
                        <template #default="{ row }">
                            <el-tag v-if="row.audience_status" :type="getStatusTagType(row.audience_status)"
                                size="small">
                                {{ getStatusText(row.audience_status) }}
                            </el-tag>
                            <span v-else class="empty-value">未知</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="用户数量" width="100">
                        <template #default="{ row }">
                            <span v-if="row.user_count > 0">{{ row.user_count }} 人</span>
                            <span v-else class="empty-value">暂无</span>
                        </template>
                    </el-table-column>

                    <el-table-column prop="bind_time" label="绑定时间" width="180" />

                    <el-table-column label="操作" width="120" fixed="right">
                        <template #default="scope">
                            <el-button type="danger" size="small" @click="unbindAudience(scope.row)"
                                :loading="unbindLoading[scope.row.id]">
                                <el-icon>
                                    <Unlock />
                                </el-icon>
                                解绑
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 分页 -->
                <div class="pagination-container" v-if="audiencePagination.total > 0">
                    <el-pagination v-model:current-page="audiencePagination.page"
                        v-model:page-size="audiencePagination.pageSize" :total="audiencePagination.total"
                        :page-sizes="[5, 10, 20]" layout="total, sizes, prev, pager, next, jumper"
                        @size-change="fetchCardAudiences" @current-change="fetchCardAudiences" />
                </div>
            </div>

            <template #footer>
                <el-button @click="adDialogVisible = false">关闭</el-button>
            </template>
        </el-dialog>

        <!-- 新增/编辑名片对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="800px" :close-on-click-modal="false">
            <el-form ref="cardForm" :model="cardForm" label-width="120px" :rules="formRules" label-position="top">
                <el-row :gutter="20">
                    <el-col :span="12">
                        <!-- 替换原来的关联用户表单项 -->
                        <el-form-item label="关联用户" prop="user_id">
                            <el-select v-model="cardForm.user_id" placeholder="请选择关联用户" filterable style="width: 100%"
                                @change="handleUserChange" :loading="userLoading" :disabled="isEditMode">
                                <el-option v-for="user in userList" :key="user.id"
                                    :label="`${user.nickname || '未设置昵称'} (${user.phone})`" :value="user.id">
                                    <div class="user-option">
                                        <el-avatar :size="24" :src="user.avatar" style="margin-right: 10px;">
                                            {{ user.nickname ? user.nickname.charAt(0) : 'U' }}
                                        </el-avatar>
                                        <span>{{ user.nickname || '未设置昵称' }}</span>
                                        <span style="color: #909399; margin-left: 10px;">{{ user.phone }}</span>
                                    </div>
                                </el-option>
                            </el-select>
                            <div class="form-tip">选择已注册的用户关联名片{{ isEditMode ? '（创建后不可修改）' : '' }}</div>
                        </el-form-item>

                        <el-form-item label="关联TX广告">
                            <el-select v-model="cardForm.gzh_id" placeholder="请选择TX广告授权" filterable style="width: 100%"
                                :loading="gzhLoading" clearable>
                                <el-option v-for="gzh in gzhList" :key="gzh.id"
                                    :label="`${gzh.ghz_name} (${gzh.ghz_appid})`" :value="gzh.id">
                                    <div class="gzh-option">
                                        <span>{{ gzh.ghz_name }}</span>
                                        <span style="color: #909399; margin-left: 10px;">{{ gzh.ghz_appid }}</span>
                                    </div>
                                </el-option>
                            </el-select>
                            <div class="form-tip">选择已授权的TX广告（可选）</div>
                        </el-form-item>

                        <el-form-item label="姓名" prop="name">
                            <el-input v-model="cardForm.name" placeholder="请输入姓名" size="large" />
                        </el-form-item>

                        <el-form-item label="职位">
                            <el-input v-model="cardForm.title" placeholder="请输入职位" size="large" />
                        </el-form-item>

                        <el-form-item label="公司">
                            <el-input v-model="cardForm.company" placeholder="请输入公司名称" size="large" />
                        </el-form-item>

                        <el-form-item label="标签">
                            <el-input v-model="cardForm.tag" placeholder="如：创始人、合伙人等" size="large" />
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <!-- 在名片对话框的表单中，替换原来的头像URL表单项 -->
                        <el-form-item label="头像">
                            <div class="avatar-upload">
                                <!-- 显示当前头像 -->
                                <div class="avatar-preview" v-if="cardForm.avatar">
                                    <el-image :src="cardForm.avatar" class="avatar-image"
                                        :preview-src-list="[cardForm.avatar]" fit="cover" />
                                    <div class="avatar-actions">
                                        <el-button type="danger" size="small" @click="removeAvatar">
                                            <el-icon>
                                                <Delete />
                                            </el-icon>
                                            删除
                                        </el-button>
                                    </div>
                                </div>

                                <!-- 上传组件 -->
                                <el-upload class="avatar-uploader" :auto-upload="false" :show-file-list="false"
                                    :on-change="handleAvatarChange" accept="image/*" :disabled="avatarUploadLoading">
                                    <el-button type="primary" :loading="avatarUploadLoading">
                                        <el-icon>
                                            <Upload />
                                        </el-icon>
                                        {{ cardForm.avatar ? '更换头像' : '上传头像' }}
                                    </el-button>
                                    <template #tip>
                                        <div class="el-upload__tip">
                                            支持 jpg、png、gif 格式，大小不超过 20MB
                                        </div>
                                    </template>
                                </el-upload>
                            </div>
                        </el-form-item>

                        <el-form-item label="电话">
                            <el-input v-model="cardForm.phone" placeholder="请输入联系电话" size="large" />
                        </el-form-item>

                        <el-form-item label="邮箱">
                            <el-input v-model="cardForm.email" placeholder="请输入邮箱地址" size="large" />
                        </el-form-item>

                        <el-form-item label="微信号">
                            <el-input v-model="cardForm.wechat_id" placeholder="请输入微信号" size="large" />
                        </el-form-item>

                        <el-form-item label="视频号">
                            <el-input v-model="cardForm.video_account" placeholder="请输入视频号" size="large" />
                        </el-form-item>

                        <el-form-item label="个人主页">
                            <el-input v-model="cardForm.personal_url" placeholder="请输入个人主页链接" size="large" />
                        </el-form-item>

                        <el-form-item label="地址">
                            <el-input v-model="cardForm.address" placeholder="请输入地址" type="textarea" :rows="2" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-divider content-position="left">数据统计</el-divider>

                <el-row :gutter="20">
                    <el-col :span="8">
                        <el-form-item label="行业经验（年）">
                            <el-input-number v-model="cardForm.experience_years" :min="0" :max="50" size="large" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="服务客户数">
                            <el-input-number v-model="cardForm.served_customers" :min="0" :max="10000" size="large" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="满意度（%）">
                            <el-input-number v-model="cardForm.satisfaction_rate" :min="0" :max="100" :precision="1"
                                size="large" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-form-item label="公开状态">
                    <el-switch v-model="cardForm.is_public" active-text="公开" inactive-text="私密" />
                    <div class="form-tip">公开的名片可以通过链接访问</div>
                </el-form-item>

                <el-form-item label="排序">
                    <el-input-number v-model="cardForm.sort_order" :min="0" :max="999" size="large" />
                    <div class="form-tip">数字越小排序越靠前</div>
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitCardForm" :loading="submitting">确认</el-button>
            </template>
        </el-dialog>

        <!-- 产品管理对话框 -->
        <el-dialog v-model="productDialogVisible" title="产品管理" width="900px" :close-on-click-modal="false">
            <div class="product-header">
                <h3>{{ currentCard?.name }} - 产品列表</h3>
                <el-button type="primary" @click="openAddProductDialog">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    添加产品
                </el-button>
            </div>

            <el-table :data="productList" stripe style="width: 100%; margin-top: 20px" v-loading="productLoading">
                <el-table-column label="产品图片" width="100" align="center">
                    <template #default="{ row }">
                        <el-image :src="row.product_image" :alt="row.product_title"
                            style="width: 60px; height: 60px; border-radius: 4px;">
                            <template #error>
                                <div class="image-slot">无图</div>
                            </template>
                        </el-image>
                    </template>
                </el-table-column>

                <el-table-column prop="product_title" label="产品标题" width="200" />
                <el-table-column prop="product_desc" label="产品描述" show-overflow-tooltip />
                <el-table-column prop="product_price" label="价格" width="120" align="center" />
                <el-table-column label="评分" width="100" align="center">
                    <template #default="{ row }">
                        <el-rate v-model="row.product_rating" disabled show-score text-color="#ff9900"
                            score-template="{value} 分" />
                    </template>
                </el-table-column>
                <el-table-column prop="product_tag" label="标签" width="120" align="center">
                    <template #default="{ row }">
                        <el-tag v-if="row.product_tag" type="success" size="small">{{ row.product_tag }}</el-tag>
                        <span v-else class="empty-value">无</span>
                    </template>
                </el-table-column>
                <el-table-column label="状态" width="80" align="center">
                    <template #default="{ row }">
                        <el-tag :type="row.is_active ? 'success' : 'info'" size="small">
                            {{ row.is_active ? '激活' : '禁用' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="sort_order" label="排序" width="80" align="center" />

                <el-table-column label="操作" width="150" fixed="right">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openEditProductDialog(scope.row)">
                            <el-icon>
                                <Edit />
                            </el-icon>
                        </el-button>
                        <el-button type="danger" size="small" @click="handleDeleteProduct(scope.row.id)">
                            <el-icon>
                                <Delete />
                            </el-icon>
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <template #footer>
                <el-button @click="productDialogVisible = false">关闭</el-button>
            </template>
        </el-dialog>

        <!-- 添加/编辑产品对话框 -->
        <el-dialog v-model="productFormDialogVisible" :title="productFormTitle" width="600px"
            :close-on-click-modal="false">
            <el-form ref="productForm" :model="productForm" label-width="100px" :rules="productFormRules">
                <el-form-item label="产品标题" prop="product_title">
                    <el-input v-model="productForm.product_title" placeholder="请输入产品标题" />
                </el-form-item>

                <el-form-item label="产品描述">
                    <el-input v-model="productForm.product_desc" placeholder="请输入产品描述" type="textarea" :rows="3" />
                </el-form-item>

                <!-- 在产品对话框的表单中，替换原来的产品图片表单项 -->
                <el-form-item label="产品图片">
                    <div class="image-upload">
                        <!-- 显示当前产品图片 -->
                        <div class="image-preview" v-if="productForm.product_image">
                            <el-image :src="productForm.product_image" class="product-image"
                                :preview-src-list="[productForm.product_image]" fit="cover" />
                            <div class="image-actions">
                                <el-button type="danger" size="small" @click="removeProductImage">
                                    <el-icon>
                                        <Delete />
                                    </el-icon>
                                    删除
                                </el-button>
                            </div>
                        </div>

                        <!-- 上传组件 -->
                        <el-upload class="image-uploader" :auto-upload="false" :show-file-list="false"
                            :on-change="handleProductImageChange" accept="image/*"
                            :disabled="productImageUploadLoading">
                            <el-button type="primary" :loading="productImageUploadLoading">
                                <el-icon>
                                    <Upload />
                                </el-icon>
                                {{ productForm.product_image ? '更换图片' : '上传图片' }}
                            </el-button>
                            <template #tip>
                                <div class="el-upload__tip">
                                    支持 jpg、png、gif 格式，大小不超过 20MB
                                </div>
                            </template>
                        </el-upload>
                    </div>
                </el-form-item>

                <el-form-item label="价格">
                    <el-input v-model="productForm.product_price" placeholder="如：¥199、免费、定制等" />
                </el-form-item>

                <el-form-item label="评分">
                    <el-rate v-model="productForm.product_rating" show-score allow-half :max="5" text-color="#ff9900"
                        score-template="{value}" />
                </el-form-item>

                <el-form-item label="产品标签">
                    <el-input v-model="productForm.product_tag" placeholder="如：热销、新品、推荐等" />
                </el-form-item>

                <el-form-item label="产品链接">
                    <el-input v-model="productForm.product_url" placeholder="请输入产品详情链接" />
                </el-form-item>

                <el-form-item label="排序">
                    <el-input-number v-model="productForm.sort_order" :min="0" :max="999" />
                </el-form-item>

                <el-form-item label="状态">
                    <el-switch v-model="productForm.is_active" active-text="激活" inactive-text="禁用" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="productFormDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitProductForm" :loading="productSubmitting">确认</el-button>
            </template>
        </el-dialog>

        <!-- 二维码预览弹窗 -->
        <el-dialog v-model="qrcodePreviewVisible" :title="qrcodePreviewTitle" width="400px" align-center
            :close-on-click-modal="false">
            <div class="qrcode-preview-container">
                <img :src="qrcodePreviewUrl" alt="小程序二维码" class="preview-qrcode-image" />
                <div class="preview-actions">
                    <el-button type="primary"
                        @click="downloadQrcode(qrcodePreviewUrl, qrcodePreviewTitle.replace(' - 小程序二维码', ''))">
                        <el-icon>
                            <Download />
                        </el-icon>
                        下载
                    </el-button>
                    <el-button @click="copyUrl(qrcodePreviewUrl)">
                        <el-icon>
                            <DocumentCopy />
                        </el-icon>
                        复制链接
                    </el-button>
                </div>
                <div class="preview-tip">
                    <el-icon>
                        <InfoFilled />
                    </el-icon>
                    此二维码永久有效，扫描可直接进入小程序
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Search, Plus, Edit, Delete, Refresh, Star, StarFilled, Goods, Upload,
    Promotion, Unlock, Picture, Download, DocumentCopy, Loading, InfoFilled
} from '@element-plus/icons-vue'


// 导入新组件
import TxAudienceTab from '@/components/TxAudienceTab.vue';

import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils';
import { ElLoading } from 'element-plus';

export default {
    name: 'BusinessCardManagement',
    components: {
        Search, Plus, Edit, Delete, Refresh, Star, StarFilled, Goods, Upload,
        Promotion, Unlock, Picture, Download, DocumentCopy, Loading, InfoFilled,
        TxAudienceTab
    },
    data() {
        return {
            loading: false,
            submitting: false,
            userLoading: false,
            gzhLoading: false,
            productLoading: false,
            productSubmitting: false,
            dialogVisible: false,
            productDialogVisible: false,
            productFormDialogVisible: false,
            dialogTitle: '新增名片',
            productFormTitle: '添加产品',
            isEditMode: false,
            isProductEditMode: false,
            searchForm: {
                name: ''
            },
            // 新增小程序二维码相关数据
            miniprogramConfig: null, // 小程序配置
            qrcodeLoading: false, // 全局二维码加载状态

            // 小程序二维码预览弹窗
            qrcodePreviewVisible: false,
            qrcodePreviewUrl: '',
            qrcodePreviewTitle: '',
            // 新增广告相关数据
            adDialogVisible: false,
            activePlatform: 'tx',
            activeTxTab: 'openid',
            currentCard: null,

            // 已绑定人群数据
            boundAudiences: [],
            audienceLoading: false,
            audiencePagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            unbindLoading: {},
            cardForm: {
                id: null,
                user_id: null,
                gzh_id: null,
                name: '',
                title: '',
                company: '',
                tag: '',
                phone: '',
                email: '',
                address: '',
                wechat_id: '',
                video_account: '',
                personal_url: '',
                avatar: '',
                experience_years: 0,
                served_customers: 0,
                satisfaction_rate: 0,
                is_public: true,
                sort_order: 0
            },
            productForm: {
                id: null,
                product_title: '',
                product_desc: '',
                product_image: '',
                product_price: '',
                product_rating: 0,
                product_tag: '',
                product_url: '',
                sort_order: 0,
                is_active: true
            },
            formRules: {
                user_id: [
                    { required: true, message: '请选择关联用户', trigger: 'change' }
                ],
                name: [
                    { required: true, message: '请输入姓名', trigger: 'blur' },
                    { min: 2, max: 20, message: '姓名长度在 2 到 20 个字符', trigger: 'blur' }
                ],
                email: [
                    { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
                ]
            },
            productFormRules: {
                product_title: [
                    { required: true, message: '请输入产品标题', trigger: 'blur' }
                ]
            },
            cardList: [],
            userList: [],
            gzhList: [],
            productList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            stats: {
                total: 0,
                public: 0
            },

            avatarUploadLoading: false,
            productImageUploadLoading: false
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        },
        primaryCardName() {
            const primaryCard = this.cardList.find(card => card.is_primary)
            return primaryCard ? primaryCard.name : '未设置'
        },
        // 检查是否已配置小程序
        hasMiniprogramConfig() {
            return this.miniprogramConfig &&
                this.miniprogramConfig.appid &&
                this.miniprogramConfig.secret
        }
    },
    mounted() {
        this.fetchCardList()
        this.fetchUserList()
        this.fetchGzhList()
        this.fetchMiniprogramConfig() // 新增：获取小程序配置

    },
    methods: {
        // 处理头像选择变化
        async handleAvatarChange(file) {
            try {
                this.avatarUploadLoading = true;
                this.$message.info('头像上传中，请稍候...');
                const url = await this.uploadImage(file.raw);
                this.cardForm.avatar = url;
                this.$message.success('头像上传成功');
            } catch (error) {
                this.$message.error(`头像上传失败: ${error.message}`);
                console.error('头像上传错误:', error);
            } finally {
                this.avatarUploadLoading = false;
            }
        },

        // 处理产品图片选择变化
        async handleProductImageChange(file) {
            try {
                this.productImageUploadLoading = true;
                this.$message.info('产品图片上传中，请稍候...');
                const url = await this.uploadImage(file.raw);
                this.productForm.product_image = url;
                this.$message.success('产品图片上传成功');
            } catch (error) {
                this.$message.error(`产品图片上传失败: ${error.message}`);
                console.error('产品图片上传错误:', error);
            } finally {
                this.productImageUploadLoading = false;
            }
        },

        // 获取小程序配置
        async fetchMiniprogramConfig() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/platform_management.php`,
                    {
                        action: 'get_all_settings',
                        store_id: this.userInfo.id
                    }
                );

                if (response.data.data && response.data.data.miniprogram) {
                    this.miniprogramConfig = response.data.data.miniprogram;

                }
            } catch (error) {
                console.error('获取小程序配置失败:', error);
            }
        },

        // 生成小程序二维码
        async generateMiniprogramQrcode(row) {
            try {
                // 设置加载状态
                row.qrcode_loading = true;

                // 修改判断条件：同时检查文件名和URL
                const hasExistingQrcode = !!(row.miniprogram_qrcode_filename || row.miniprogram_qrcode);
                const forceRegenerate = hasExistingQrcode;

                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'generate_miniprogram_qrcode',
                        store_id: this.userInfo.id,
                        card_id: row.id,
                        force: forceRegenerate // 如果已有二维码，则强制重新生成
                    }
                );

                if (response.data.success) {
                    // 更新名片的二维码数据
                    row.miniprogram_qrcode = response.data.qrcode_url;
                    row.miniprogram_qrcode_last_generate_time = response.data.generate_time;
                    row.miniprogram_qrcode_generate_count = response.data.generate_count;

                    this.$message.success(response.data.is_cached ? '已加载缓存的二维码' : '二维码生成成功');

                    // 如果是缓存的二维码，更新按钮文本
                    if (response.data.is_cached && this.$refs.qrcodeButton) {
                        // 可能需要更新按钮状态
                    }
                } else {
                    this.$message.error(response.data.error || '生成失败');
                }
            } catch (error) {
                console.error('生成小程序二维码失败:', error);
                let errorMsg = '生成失败';
                if (error.response && error.response.data && error.response.data.error) {
                    errorMsg = error.response.data.error;
                }
                this.$message.error(errorMsg);
            } finally {
                row.qrcode_loading = false;
            }
        },

        // 预览二维码
        previewQrcode(url, name) {
            this.qrcodePreviewUrl = url;
            this.qrcodePreviewTitle = `${name} - 小程序二维码`;
            this.qrcodePreviewVisible = true;
        },

        // 下载二维码
        downloadQrcode(url, name) {
            try {
                const link = document.createElement('a');
                link.href = url;
                link.download = `小程序二维码_${name || '名片'}_${Date.now()}.png`;
                link.target = '_blank';

                document.body.appendChild(link);
                link.click();

                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                }, 100);

                this.$message.success('二维码下载成功');
            } catch (error) {
                console.error('下载二维码失败:', error);
                this.$message.error('下载失败，请重试');
            }
        },
        // 上传图片到OSS
        async uploadImage(file) {
            try {
                const loading = ElLoading.service({
                    lock: true,
                    text: '图片上传中...',
                    spinner: 'el-icon-loading',
                    background: 'rgba(0, 0, 0, 0.7)'
                });

                // 文件验证
                const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
                const maxSize = 20 * 1024 * 1024; // 20MB

                if (allowedTypes.indexOf(file.type) === -1) {
                    throw new Error(`不支持的文件类型: ${file.type}，仅支持 ${allowedTypes.join(', ')}`);
                }

                if (file.size > maxSize) {
                    throw new Error(`文件大小超过限制 (20MB): ${(file.size / 1024 / 1024).toFixed(2)}MB`);
                }

                const signature = await getUploadSignature(file.type.split('/')[0]);
                const res = await uploadToOSS(file, signature);

                loading.close();
                return res.url;
            } catch (error) {
                ElLoading.service().close();
                console.error('图片上传失败:', error);
                throw error;
            }
        },

        // 删除头像
        removeAvatar() {
            this.cardForm.avatar = '';
        },

        // 删除产品图片
        removeProductImage() {
            this.productForm.product_image = '';
        },

        // 打开广告管理对话框
        openAdDialog(row) {
            this.currentCard = row;
            this.adDialogVisible = true;
            this.activePlatform = 'tx';
            this.activeTxTab = 'openid';
            this.fetchCardAudiences();

            // 延迟加载子组件数据
            this.$nextTick(() => {
                if (this.$refs.openidTab) {
                    this.$refs.openidTab.fetchAvailableAudiences();
                }
            });
        },

        // 获取已绑定人群
        async fetchCardAudiences() {
            if (!this.currentCard) return;

            this.audienceLoading = true;
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'get_card_audiences',
                        store_id: this.userInfo.id,
                        card_id: this.currentCard.id,
                        page: this.audiencePagination.page,
                        page_size: this.audiencePagination.pageSize
                    }
                );

                if (response.data.data) {
                    this.boundAudiences = response.data.data;
                    this.audiencePagination.total = response.data.pagination?.total || 0;
                }
            } catch (error) {
                console.error('获取已绑定人群错误:', error);
                this.boundAudiences = [];
            } finally {
                this.audienceLoading = false;
            }
        },

        // 解绑人群
        async unbindAudience(audience) {
            try {
                await this.$confirm('确定要解绑这个人群吗？', '确认解绑', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                });

                this.unbindLoading = { ...this.unbindLoading, [audience.id]: true };

                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'unbind_audience',
                        store_id: this.userInfo.id,
                        bind_id: audience.id
                    }
                );

                if (response.data.success) {
                    this.$message.success('解绑成功');
                    // 刷新已绑定人群列表
                    this.fetchCardAudiences();

                    // 刷新对应的子组件数据
                    this.$nextTick(() => {
                        if (this.activeTxTab === 'openid' && this.$refs.openidTab) {
                            this.$refs.openidTab.fetchAvailableAudiences();
                        } else if (this.activeTxTab === 'mobile' && this.$refs.mobileTab) {
                            this.$refs.mobileTab.fetchAvailableAudiences();
                        }
                    });
                } else {
                    this.$message.error(response.data.error || '解绑失败');
                }
            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('解绑失败');
                    console.error('解绑错误:', error);
                }
            } finally {
                this.unbindLoading = { ...this.unbindLoading, [audience.id]: false };
            }
        },

        // 1. 修改handlePlatformChange方法，添加数据刷新
        handlePlatformChange(tab) {
            if (tab.props.name !== 'tx') {
                this.$message.info('程序猿正在紧急开发中···，请稍后！');
            } else {
                // 切换到腾讯广告Tab时，刷新已绑定人群数据
                this.fetchCardAudiences();
            }
        },

        // 2. 添加处理腾讯子Tab切换的方法
        handleTxTabChange(tabName) {
            // 切换OpenID/手机号Tab时，刷新对应子组件的数据
            this.$nextTick(() => {
                if (tabName === 'openid' && this.$refs.openidTab) {
                    this.$refs.openidTab.fetchAvailableAudiences();
                } else if (tabName === 'mobile' && this.$refs.mobileTab) {
                    this.$refs.mobileTab.fetchAvailableAudiences();
                }
            });
        },

        // 联系客服
        handleContact() {
            this.$message.info('请联系客服获取最新开发进度');
        },

        // 平台标签样式
        getPlatformTagType(platform) {
            const typeMap = {
                'tx': 'primary',
                'douyin': 'danger',
                'baidu': 'warning',
                'xiaohongshu': 'success'
            };
            return typeMap[platform] || 'info';
        },

        getPlatformText(platform) {
            const textMap = {
                'tx': '腾讯广告',
                'douyin': '抖音广告',
                'baidu': '百度广告',
                'xiaohongshu': '小红书'
            };
            return textMap[platform] || platform;
        },

        // 人群状态样式
        getStatusTagType(status) {
            const typeMap = {
                'SUCCESS': 'success',
                'PROCESSING': 'warning',
                'PENDING': 'info',
                'ERROR': 'danger',
                'DELETED': 'danger'
            };
            return typeMap[status] || 'info';
        },

        getStatusText(status) {
            const textMap = {
                'SUCCESS': '活跃',
                'PROCESSING': '处理中',
                'PENDING': '等待中',
                'ERROR': '错误',
                'DELETED': '已删除'
            };
            return textMap[status] || status;
        },

        getAudienceTypeText(type) {
            const textMap = {
                'CUSTOMER_FILE': '客户文件',
                'LOOKALIKE': 'Lookalike',
                'USER_ACTION': '用户行为',
                'KEYWORD': '关键词',
                'COMBINE': '组合'
            };
            return textMap[type] || type;
        },

        // 获取名片列表
        async fetchCardList() {
            this.loading = true
            try {
                const params = {
                    action: 'list',
                    store_id: this.userInfo.id,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize,
                    with_qrcode_info: true  // 新增参数：获取二维码信息
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    params
                )

                if (data.data) {
                    // 转换数据类型
                    this.cardList = data.data.map(card => ({
                        ...card,
                        is_public: Boolean(card.is_public),
                        is_primary: Boolean(card.is_primary),
                        qrcode_loading: false, // 新增：初始化二维码加载状态
                        // 确保 miniprogram_qrcode 字段正确设置
                        miniprogram_qrcode: card.miniprogram_qrcode_url || card.miniprogram_qrcode || '',
                        // 保留其他字段用于判断
                        miniprogram_qrcode_filename: card.miniprogram_qrcode_filename,
                        miniprogram_qrcode_url: card.miniprogram_qrcode_url
                    }))
                    this.pagination.total = data.pagination?.total || 0
                    this.updateStats()
                }
            } catch (error) {
                this.$message.error('获取名片列表失败')
                console.error('获取名片列表错误:', error)
            } finally {
                this.loading = false
            }
        },

        // 获取用户列表
        async fetchUserList() {
            this.userLoading = true
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'get_users',
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.data) {
                    this.userList = response.data.data
                }
            } catch (error) {
                console.error('获取用户列表错误:', error)
            } finally {
                this.userLoading = false
            }
        },

        // 获取TX广告授权列表
        async fetchGzhList() {
            this.gzhLoading = true
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'get_gzh_list',
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.data) {
                    this.gzhList = response.data.data
                }
            } catch (error) {
                console.error('获取TX广告列表错误:', error)
            } finally {
                this.gzhLoading = false
            }
        },

        // 获取产品列表
        async fetchProductList(cardId) {
            this.productLoading = true
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'get_products',
                        store_id: this.userInfo.id,
                        card_id: cardId
                    }
                )

                if (response.data.data) {
                    // 转换数据类型
                    this.productList = response.data.data.map(product => ({
                        ...product,
                        is_active: Boolean(product.is_active),
                        product_rating: parseFloat(product.product_rating) || 0
                    }))
                }
            } catch (error) {
                console.error('获取产品列表错误:', error)
                this.productList = []
            } finally {
                this.productLoading = false
            }
        },

        // 更新统计信息
        updateStats() {
            this.stats.total = this.cardList.length
            this.stats.public = this.cardList.filter(card => card.is_public).length
        },

        // 处理搜索
        handleSearch() {
            this.pagination.page = 1
            if (this.searchForm.name) {
                this.searchCards()
            } else {
                this.fetchCardList()
            }
        },

        // 搜索名片
        async searchCards() {
            this.loading = true
            try {
                const params = {
                    action: 'search',
                    store_id: this.userInfo.id,
                    name: this.searchForm.name,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    params
                )

                if (data.data) {
                    this.cardList = data.data.map(card => ({
                        ...card,
                        is_public: Boolean(card.is_public),
                        is_primary: Boolean(card.is_primary)
                    }))
                    this.pagination.total = data.pagination?.total || 0
                    this.updateStats()
                }
            } catch (error) {
                this.$message.error('搜索失败')
                console.error('搜索错误:', error)
            } finally {
                this.loading = false
            }
        },

        // 用户选择变化
        handleUserChange(userId) {
            const selectedUser = this.userList.find(user => user.id === userId)
            if (selectedUser) {
                this.cardForm.name = selectedUser.nickname || ''
                this.cardForm.avatar = selectedUser.avatar || ''
                this.cardForm.phone = selectedUser.phone || ''
            }
        },

        // 打开新增名片对话框
        openAddDialog() {
            this.dialogTitle = '新增名片'
            this.isEditMode = false
            this.cardForm = {
                id: null,
                user_id: null,
                gzh_id: null,
                name: '',
                title: '',
                company: '',
                tag: '',
                phone: '',
                email: '',
                address: '',
                wechat_id: '',
                video_account: '',
                personal_url: '',
                avatar: '',
                experience_years: 0,
                served_customers: 0,
                satisfaction_rate: 0,
                is_public: true,
                sort_order: 0
            }
            this.dialogVisible = true

            this.$nextTick(() => {
                if (this.$refs.cardForm) {
                    this.$refs.cardForm.clearValidate()
                }
            })
        },

        // 打开编辑名片对话框
        openEditDialog(row) {
            this.dialogTitle = '编辑名片'
            this.isEditMode = true
            this.cardForm = { ...row }
            this.dialogVisible = true

            this.$nextTick(() => {
                if (this.$refs.cardForm) {
                    this.$refs.cardForm.clearValidate()
                }
            })
        },

        // 打开产品管理对话框
        openProductDialog(row) {
            this.currentCard = row
            this.productDialogVisible = true
            this.fetchProductList(row.id)
        },

        // 打开添加产品对话框
        openAddProductDialog() {
            this.productFormTitle = '添加产品'
            this.isProductEditMode = false
            this.productForm = {
                id: null,
                product_title: '',
                product_desc: '',
                product_image: '',
                product_price: '',
                product_rating: 0,
                product_tag: '',
                product_url: '',
                sort_order: 0,
                is_active: true
            }
            this.productFormDialogVisible = true

            this.$nextTick(() => {
                if (this.$refs.productForm) {
                    this.$refs.productForm.clearValidate()
                }
            })
        },

        // 打开编辑产品对话框
        openEditProductDialog(row) {
            this.productFormTitle = '编辑产品'
            this.isProductEditMode = true
            this.productForm = {
                ...row,
                is_active: Boolean(row.is_active),
                product_rating: parseFloat(row.product_rating) || 0
            }
            this.productFormDialogVisible = true

            this.$nextTick(() => {
                if (this.$refs.productForm) {
                    this.$refs.productForm.clearValidate()
                }
            })
        },

        // 提交名片表单
        async submitCardForm() {
            this.$refs.cardForm.validate(async (valid) => {
                if (valid) {
                    this.submitting = true
                    try {
                        const action = this.isEditMode ? 'update' : 'add'
                        const payload = {
                            ...this.cardForm,
                            store_id: this.userInfo.id
                        }

                        const response = await axios.post(
                            `${config.BASE_URL}/shop/api/business_card_management.php`,
                            {
                                action,
                                ...payload
                            }
                        )

                        if (response.data.error) {
                            this.$message.error(response.data.error)
                        } else {
                            this.$message.success(this.isEditMode ? '更新成功' : '添加成功')
                            this.dialogVisible = false
                            this.fetchCardList()
                        }
                    } catch (error) {
                        let errorMsg = '操作失败'
                        if (error.response && error.response.data && error.response.data.error) {
                            errorMsg = error.response.data.error
                        }
                        this.$message.error(errorMsg)
                        console.error('表单提交错误:', error)
                    } finally {
                        this.submitting = false
                    }
                }
            })
        },

        // 提交产品表单
        async submitProductForm() {
            this.$refs.productForm.validate(async (valid) => {
                if (valid) {
                    this.productSubmitting = true
                    try {
                        const action = this.isProductEditMode ? 'update_product' : 'add_product'
                        const payload = {
                            ...this.productForm,
                            store_id: this.userInfo.id,
                            card_id: this.currentCard.id,
                            is_active: this.productForm.is_active ? 1 : 0,
                            product_rating: parseFloat(this.productForm.product_rating) || 0
                        }

                        const response = await axios.post(
                            `${config.BASE_URL}/shop/api/business_card_management.php`,
                            {
                                action,
                                ...payload
                            }
                        )

                        if (response.data.error) {
                            this.$message.error(response.data.error)
                        } else {
                            this.$message.success(this.isProductEditMode ? '产品更新成功' : '产品添加成功')
                            this.productFormDialogVisible = false
                            this.fetchProductList(this.currentCard.id)
                        }
                    } catch (error) {
                        let errorMsg = '操作失败'
                        if (error.response && error.response.data && error.response.data.error) {
                            errorMsg = error.response.data.error
                        }
                        this.$message.error(errorMsg)
                        console.error('产品表单提交错误:', error)
                    } finally {
                        this.productSubmitting = false
                    }
                }
            })
        },

        // 删除名片
        async handleDelete(id) {
            this.$confirm('确定要删除该名片吗？', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/business_card_management.php`,
                        {
                            action: 'delete',
                            id: id,
                            store_id: this.userInfo.id
                        }
                    )

                    if (response.data.error) {
                        this.$message.error(response.data.error)
                    } else {
                        this.$message.success('删除成功')
                        this.fetchCardList()
                    }
                } catch (error) {
                    let errorMsg = '删除失败'
                    if (error.response && error.response.data && error.response.data.error) {
                        errorMsg = error.response.data.error
                    }
                    this.$message.error(errorMsg)
                    console.error('删除错误:', error)
                }
            }).catch(() => { })
        },

        // 删除产品
        async handleDeleteProduct(id) {
            this.$confirm('确定要删除该产品吗？', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/business_card_management.php`,
                        {
                            action: 'delete_product',
                            id: id,
                            store_id: this.userInfo.id
                        }
                    )

                    if (response.data.error) {
                        this.$message.error(response.data.error)
                    } else {
                        this.$message.success('产品删除成功')
                        this.fetchProductList(this.currentCard.id)
                    }
                } catch (error) {
                    let errorMsg = '删除失败'
                    if (error.response && error.response.data && error.response.data.error) {
                        errorMsg = error.response.data.error
                    }
                    this.$message.error(errorMsg)
                    console.error('删除产品错误:', error)
                }
            }).catch(() => { })
        },

        // 设置为主名片
        async setAsPrimary(row) {
            if (row.is_primary) {
                return
            }

            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'set_primary',
                        id: row.id,
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.error) {
                    this.$message.error(response.data.error)
                } else {
                    this.$message.success('设置主名片成功')
                    this.fetchCardList()
                }
            } catch (error) {
                let errorMsg = '设置失败'
                if (error.response && error.response.data && error.response.data.error) {
                    errorMsg = error.response.data.error
                }
                this.$message.error(errorMsg)
                console.error('设置主名片错误:', error)
            }
        },

        // 生成TX广告链接
        getOffiaccountUrl(cardId) {
            const baseDomain = window.location.origin
            return `${baseDomain}/offiaccount/?store_id=${this.userInfo.id}&card_id=${cardId}`
        },

        // 生成H5链接
        getH5Url(cardId) {
            const baseDomain = window.location.origin
            return `${baseDomain}/h5/?store_id=${this.userInfo.id}&card_id=${cardId}`
        },

        // 生成小程序链接
        getMiniUrl(cardId) {
            return `pages/home/home?store_id=${this.userInfo.id}&card_id=${cardId}`
        },

        // 复制链接
        copyUrl(url) {
            navigator.clipboard.writeText(url).then(() => {
                this.$message.success('链接已复制到剪贴板')
            }).catch(() => {
                const textArea = document.createElement('textarea')
                textArea.value = url
                document.body.appendChild(textArea)
                textArea.select()
                document.execCommand('copy')
                document.body.removeChild(textArea)
                this.$message.success('链接已复制到剪贴板')
            })
        }
    }
}
</script>

<style scoped lang="scss">
.page-container {
    padding: 20px;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.stats-info {
    display: flex;
    gap: 20px;
    background: #f0f7ff;
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
}

.stat-item {
    display: flex;
    align-items: center;

    .label {
        color: #606266;
        font-weight: 500;
    }

    .value {
        font-weight: 600;
        color: #409eff;

        &.primary {
            color: #67C23A;
        }
    }
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.5px;
}

.search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

.product-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #ebeef5;

    h3 {
        margin: 0;
        color: #1f2937;
    }
}

.el-table {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;

    :deep(.el-table__header) th {
        background-color: #f5f7fa;
        color: #606266;
        font-weight: 600;
    }

    :deep(.el-table__body) tr:hover>td {
        background-color: #f0f7ff !important;
    }
}

.primary-icon {
    transition: all 0.3s ease;

    &:hover {
        transform: scale(1.2);
    }
}

.user-info {
    .user-nickname {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .user-phone {
        color: #909399;
        font-size: 12px;
    }
}

.gzh-info {
    .gzh-name {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .gzh-appid {
        color: #909399;
        font-size: 12px;
    }
}

.user-option,
.gzh-option {
    display: flex;
    align-items: center;
}

.card-url-section {
    display: flex;
    align-items: center;
    gap: 8px;
}

.url-list {
    .url-item {
        margin-bottom: 12px;

        &:last-child {
            margin-bottom: 0;
        }

        .url-label {
            font-weight: 500;
            color: #606266;
            font-size: 14px;
        }

        .url-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-top: 4px;
            padding: 8px;
            background: #f5f7fa;
            border-radius: 4px;

            .url-text {
                flex: 1;
                font-size: 12px;
                color: #409eff;
                word-break: break-all;
                margin-right: 8px;
            }
        }
    }
}

.empty-value {
    color: #909399;
    font-style: italic;
}

.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 6px;
}

.image-slot {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: #f5f7fa;
    color: #909399;
    font-size: 12px;
}

:deep(.el-dialog) {
    border-radius: 12px;

    .el-dialog__header {
        padding: 20px 24px;
        border-bottom: 1px solid #ebeef5;
        margin-right: 0;

        .el-dialog__title {
            font-weight: 600;
            color: #1f2937;
        }
    }

    .el-dialog__body {
        padding: 24px;
    }

    .el-dialog__footer {
        padding: 16px 24px;
        border-top: 1px solid #ebeef5;
    }
}

.el-button {
    display: inline-flex;
    align-items: center;

    .el-icon {
        margin-right: 5px;
    }
}

// 头像和图片上传样式
.avatar-upload,
.image-upload {
    display: flex;
    align-items: flex-start;
    gap: 20px;
}

.avatar-preview,
.image-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.avatar-image {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    border: 2px solid #ebeef5;
    overflow: hidden;
}

.product-image {
    width: 100px;
    height: 100px;
    border-radius: 8px;
    border: 2px solid #ebeef5;
    overflow: hidden;
}

.avatar-actions,
.image-actions {
    display: flex;
    justify-content: center;
}

.avatar-uploader,
.image-uploader {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.el-upload__tip {
    font-size: 12px;
    color: #909399;
    margin-top: 5px;
}

// 广告对话框样式
.ad-audience-dialog {
    .platform-tabs {
        margin-bottom: 20px;

        :deep(.el-tabs__header) {
            margin: 0;
        }

        :deep(.el-tabs__content) {
            padding: 0;
        }
    }

    .coming-soon {
        padding: 40px 20px;
        text-align: center;
        background: #f8f9fa;
        border-radius: 8px;
        min-height: 300px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .bound-audiences-section {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #ebeef5;

        .section-title {
            font-size: 16px;
            color: #1f2937;
            margin-bottom: 15px;
            font-weight: 600;
        }
    }
}

// 响应式调整
@media (max-width: 768px) {

    .avatar-upload,
    .image-upload {
        flex-direction: column;
        align-items: center;
    }
}

/* 在小程序路径样式部分 */
.mini-qrcode-actions {
    display: flex;
    gap: 4px;
}

.qrcode-display {
    margin-top: 12px;
    padding: 12px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #ebeef5;
}

.qrcode-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    color: #606266;
    font-weight: 500;
}

.qrcode-content {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.qrcode-image {
    width: 150px;
    height: 150px;
    border-radius: 6px;
    border: 1px solid #ddd;
    margin-bottom: 8px;
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.05);
    }
}

.qrcode-actions {
    display: flex;
    gap: 12px;
    margin-top: 8px;
}

.qrcode-tip {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #909399;
    font-size: 12px;
    margin-top: 8px;
    padding: 8px;
    background: #f5f5f5;
    border-radius: 4px;
}

/* 二维码预览样式 */
.qrcode-preview-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
}

.preview-qrcode-image {
    width: 250px;
    height: 250px;
    border-radius: 8px;
    border: 2px solid #ebeef5;
    margin-bottom: 20px;
}

.preview-actions {
    display: flex;
    gap: 15px;
    margin-bottom: 15px;
}

.preview-tip {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #409eff;
    font-size: 14px;
    padding: 10px;
    background: #f0f7ff;
    border-radius: 6px;
    border: 1px solid #d9ecff;
}
</style>